import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  HostListener
} from '@angular/core';
import {
  AnclaDomGrafico,
  cerrarOverlayPantallaCompleta,
  sincronizarOverlayPantallaCompleta
} from '../../utils/grafico-fullscreen.util';
import {
  CHART_DASHBOARD_ANIMACION_ENTRADA_MS,
  CHART_DASHBOARD_OPCIONES_ANIMACION_ENTRADA
} from '../../config/chart-dashboard.config';
import {
  AvanceVisitaItem,
  luminanciaRelativaHex,
  obtenerColorAvanceVisitas,
  SERIES_AVANCE_VISITAS_DASHBOARD
} from '../../config/colores-visitas.config';
import { formatChileanNumber } from '../../utils/numeros.util';
import {
  ColumnaTablaFullscreen,
  construirTablaDistribucion
} from '../../utils/grafico-fullscreen-tabla.util';

type Chart = any;

@Component({
  selector: 'app-grafico-avance-visitas',
  templateUrl: './grafico-avance-visitas.component.html',
  styleUrls: ['./grafico-avance-visitas.component.scss'],
  standalone: false
})
export class GraficoAvanceVisitasComponent implements AfterViewChecked, OnChanges, OnDestroy {
  private static contadorId = 0;
  public idUnico: string;

  @ViewChild('graficoCanvas', { static: false }) graficoCanvasRef!: ElementRef;
  @ViewChild('pantallaCompletaRoot', { static: false }) pantallaCompletaRoot!: ElementRef<HTMLElement>;

  @Input() titulo: string = 'Avance de Visita';
  @Input() subtitulo: string = '';
  @Input() datos: AvanceVisitaItem[] = [];
  @Input() cargando: boolean = false;
  @Input() informacion: string = '';

  modalAbierto = false;

  graficoListo = false;
  columnasTablaFullscreen: ColumnaTablaFullscreen[] = [];
  filasTablaFullscreen: Record<string, string | number>[] = [];

  private grafico: Chart | null = null;
  private graficoCreado = false;
  private creandoGrafico = false;
  private chartJsCargado = false;
  private ChartClass: any = null;

  private resizeObserver: ResizeObserver | null = null;
  private resizeFrameId: number | null = null;
  private animacionEntradaEnCurso = false;
  private animacionEntradaTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private ultimoTamanoContenedor: { w: number; h: number } | null = null;
  private anclaDomOverlay: AnclaDomGrafico | null = null;

  private leyendaOcultaPorEtiqueta = new Map<string, boolean>();

  constructor() {
    this.idUnico = `informacion-grafico-avance-visitas-${GraficoAvanceVisitasComponent.contadorId++}`;
  }

  ngAfterViewChecked(): void {
    if (this.modalAbierto) {
      this.anclaDomOverlay = sincronizarOverlayPantallaCompleta(
        this.pantallaCompletaRoot?.nativeElement,
        true,
        this.anclaDomOverlay
      );
    }

    if (
      !this.graficoCreado &&
      !this.creandoGrafico &&
      this.graficoCanvasRef &&
      this.tieneDatosParaGrafico &&
      !this.cargando
    ) {
      this.creandoGrafico = true;
      void this.crearGrafico()
        .then(() => this.finalizarRenderGrafico())
        .catch(console.error)
        .finally(() => {
          this.creandoGrafico = false;
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cargando'] && this.cargando) {
      this.graficoCreado = false;
      this.graficoListo = false;
      this.creandoGrafico = false;
      this.destruirGrafico();
      return;
    }

    if (changes['datos']) {
      this.actualizarTablaFullscreen();
      if (!this.tieneDatosParaGrafico) {
        this.graficoCreado = false;
        this.graficoListo = false;
        this.destruirGrafico();
        return;
      }

      if (this.grafico) {
        this.actualizarGrafico().catch(console.error);
      } else if (this.graficoCreado) {
        this.graficoCreado = false;
      }
    }
  }

  get tieneDatosParaGrafico(): boolean {
    return this.obtenerDatosPie().length > 0;
  }

  private actualizarTablaFullscreen(): void {
    const tabla = construirTablaDistribucion(this.obtenerDatosPie());
    this.columnasTablaFullscreen = tabla.columnas;
    this.filasTablaFullscreen = tabla.filas;
  }

  get mostrarSinDatos(): boolean {
    return !this.cargando && !this.tieneDatosParaGrafico;
  }

  get mostrarGrafico(): boolean {
    return !this.cargando && this.tieneDatosParaGrafico;
  }

  private obtenerDatosPie(): AvanceVisitaItem[] {
    return (this.datos ?? []).filter(d => Number(d.value) > 0);
  }

  ngOnDestroy(): void {
    this.destruirGrafico();
    this.leyendaOcultaPorEtiqueta.clear();
    if (this.modalAbierto) {
      this.cerrarPantallaCompleta();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.modalAbierto) {
      this.cerrarPantallaCompleta();
    }
  }

  abrirPantallaCompleta(): void {
    if (this.cargando || !this.tieneDatosParaGrafico || this.modalAbierto) {
      return;
    }
    this.actualizarTablaFullscreen();
    this.modalAbierto = true;
    this.programarResizeGrafico(true);
  }

  cerrarPantallaCompleta(): void {
    if (!this.modalAbierto) {
      return;
    }
    this.modalAbierto = false;
    cerrarOverlayPantallaCompleta(this.pantallaCompletaRoot?.nativeElement, this.anclaDomOverlay);
    this.anclaDomOverlay = null;
    this.programarResizeGrafico(true);
  }

  private obtenerColor(label: string): { background: string; border: string } {
    return obtenerColorAvanceVisitas(label);
  }

  private coloresEtiquetaSobreFondo(hexFondo: string): { color: string; textStrokeColor: string } {
    const L = luminanciaRelativaHex(hexFondo || '#6b7280');
    const umbral = 0.45;
    if (L < umbral) {
      return { color: '#ffffff', textStrokeColor: 'rgba(0, 0, 0, 0.35)' };
    }
    return { color: '#1a1a1a', textStrokeColor: 'rgba(255, 255, 255, 0.85)' };
  }

  private construirEtiquetasLeyenda(chart: Chart): Record<string, unknown>[] {
    const labels = (chart.data.labels ?? []) as string[];

    return SERIES_AVANCE_VISITAS_DASHBOARD.map(serie => {
      const dataIndex = labels.indexOf(serie.label);
      let hidden: boolean;

      if (dataIndex >= 0) {
        hidden = !chart.getDataVisibility(dataIndex);
        this.leyendaOcultaPorEtiqueta.set(serie.label, hidden);
      } else {
        hidden = this.leyendaOcultaPorEtiqueta.get(serie.label) ?? false;
      }

      const item: Record<string, unknown> = {
        text: serie.label,
        fillStyle: serie.color,
        strokeStyle: serie.color,
        lineWidth: 1,
        hidden
      };

      if (dataIndex >= 0) {
        item['index'] = dataIndex;
      }

      return item;
    });
  }

  private aplicarVisibilidadDesdeMapa(chart: Chart): void {
    const labels = (chart.data.labels ?? []) as string[];

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      if (this.leyendaOcultaPorEtiqueta.get(label) === true) {
        chart.setDataVisibility(i, false);
      }
    }
  }

  private onClicLeyenda(
    _evt: unknown,
    legendItem: { text: string },
    legend: { chart: Chart }
  ): void {
    const chart = legend.chart;
    const etiqueta = legendItem.text;
    const labels = (chart.data.labels ?? []) as string[];
    const dataIndex = labels.indexOf(etiqueta);

    if (dataIndex >= 0) {
      chart.toggleDataVisibility(dataIndex);
      this.leyendaOcultaPorEtiqueta.set(etiqueta, !chart.getDataVisibility(dataIndex));
    } else {
      const actual = this.leyendaOcultaPorEtiqueta.get(etiqueta) ?? false;
      this.leyendaOcultaPorEtiqueta.set(etiqueta, !actual);
    }

    chart.update();
  }

  private async cargarChartJs(): Promise<void> {
    if (this.chartJsCargado && this.ChartClass) return;

    const [{ Chart, registerables }, ChartDataLabels] = await Promise.all([
      import('chart.js'),
      import('chartjs-plugin-datalabels')
    ]);

    Chart.register(...registerables, ChartDataLabels.default);
    this.ChartClass = Chart;
    this.chartJsCargado = true;
  }

  private construirConfiguracionGrafico() {
    const datosPie = this.obtenerDatosPie();
    const labels = datosPie.map(d => d.label);
    const values = datosPie.map(d => d.value);
    const backgroundColors = datosPie.map(d => this.obtenerColor(d.label).background);
    const borderColors = datosPie.map(d => this.obtenerColor(d.label).border);

    return {
      type: 'pie' as const,
      data: {
        labels,
        datasets: [{
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1
        }]
      },
      options: {
        animation: CHART_DASHBOARD_OPCIONES_ANIMACION_ENTRADA,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: 8
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom' as const,
            onClick: (evt: unknown, legendItem: { text: string }, legend: { chart: Chart }) =>
              this.onClicLeyenda(evt, legendItem, legend),
            labels: {
              usePointStyle: true,
              pointStyle: 'circle' as const,
              boxWidth: 8,
              boxHeight: 8,
              font: { size: 12 },
              padding: 14,
              generateLabels: (chart: Chart) => this.construirEtiquetasLeyenda(chart)
            }
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                const dataset = context.dataset.data as number[];
                const total = dataset.reduce((acc: number, val: number) => acc + val, 0);
                const percentage = total > 0 ? formatChileanNumber((value / total) * 100, 1) : '0';
                return `${label}: ${value} (${percentage}%)`;
              }
            }
          },
          datalabels: {
            color: (context: any) => {
              const bg = Array.isArray(context.dataset.backgroundColor)
                ? context.dataset.backgroundColor[context.dataIndex]
                : context.dataset.backgroundColor;
              return this.coloresEtiquetaSobreFondo(bg).color;
            },
            textStrokeColor: (context: any) => {
              const bg = Array.isArray(context.dataset.backgroundColor)
                ? context.dataset.backgroundColor[context.dataIndex]
                : context.dataset.backgroundColor;
              return this.coloresEtiquetaSobreFondo(bg).textStrokeColor;
            },
            textStrokeWidth: 1.5,
            font: {
              weight: 'bold' as const,
              size: 16
            },
            formatter: (value: number, context: any) => {
              if (value === 0) return '';
              const dataset = context.dataset.data as number[];
              const total = dataset.reduce((acc: number, val: number) => acc + val, 0);
              if (total === 0) return '';
              const percentage = formatChileanNumber((value / total) * 100, 1);
              return `${percentage}%`;
            }
          }
        }
      }
    };
  }

  private async crearGrafico(): Promise<void> {
    const ctx = this.graficoCanvasRef?.nativeElement?.getContext('2d');
    if (!ctx) return;

    await this.cargarChartJs();

    if (!this.ChartClass) {
      console.error('Chart.js no se pudo cargar');
      return;
    }

    this.destruirGrafico();
    this.iniciarAnimacionEntrada();

    const config = this.construirConfiguracionGrafico();
    const chart = new this.ChartClass(ctx, config);
    this.grafico = chart;
    this.aplicarVisibilidadDesdeMapa(chart);

    const canvas = this.graficoCanvasRef.nativeElement;
    const contenedor = canvas.closest('.grafico-contenedor') as HTMLElement | null;
    const elementoObservado = contenedor ?? canvas.parentElement;

    if (elementoObservado && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver?.disconnect();

      this.resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const { width, height } = entry.contentRect;
        const prev = this.ultimoTamanoContenedor;
        if (prev && Math.abs(prev.w - width) < 2 && Math.abs(prev.h - height) < 2) {
          return;
        }
        this.ultimoTamanoContenedor = { w: width, h: height };
        this.programarResizeGrafico();
      });
      this.resizeObserver.observe(elementoObservado);
    }
  }

  private async actualizarGrafico(): Promise<void> {
    if (!this.grafico) {
      await this.crearGrafico();
      this.finalizarRenderGrafico();
      return;
    }

    if (!this.tieneDatosParaGrafico) {
      this.graficoCreado = false;
      this.graficoListo = false;
      this.destruirGrafico();
      return;
    }

    const datosPie = this.obtenerDatosPie();
    this.grafico.data.labels = datosPie.map(d => d.label);
    this.grafico.data.datasets[0].data = datosPie.map(d => d.value);
    this.grafico.data.datasets[0].backgroundColor = datosPie.map(d =>
      this.obtenerColor(d.label).background
    );
    this.grafico.data.datasets[0].borderColor = datosPie.map(d =>
      this.obtenerColor(d.label).border
    );

    this.aplicarVisibilidadDesdeMapa(this.grafico);
    this.grafico.update('none');
    this.programarResizeGrafico();
  }

  private finalizarRenderGrafico(): void {
    this.graficoCreado = true;
    this.graficoListo = true;
  }

  private iniciarAnimacionEntrada(): void {
    this.limpiarAnimacionEntrada();
    this.animacionEntradaEnCurso = true;
    this.animacionEntradaTimeoutId = setTimeout(() => {
      this.animacionEntradaTimeoutId = null;
      this.animacionEntradaEnCurso = false;
      this.programarResizeGrafico();
    }, CHART_DASHBOARD_ANIMACION_ENTRADA_MS + 50);
  }

  private limpiarAnimacionEntrada(): void {
    if (this.animacionEntradaTimeoutId !== null) {
      clearTimeout(this.animacionEntradaTimeoutId);
      this.animacionEntradaTimeoutId = null;
    }
    this.animacionEntradaEnCurso = false;
  }

  private programarResizeGrafico(reiniciarCacheTamano = false): void {
    if (this.animacionEntradaEnCurso) {
      return;
    }

    if (reiniciarCacheTamano) {
      this.ultimoTamanoContenedor = null;
    }

    if (this.resizeFrameId !== null) {
      cancelAnimationFrame(this.resizeFrameId);
    }
    this.resizeFrameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.resizeFrameId = null;
        this.grafico?.resize();
      });
    });
  }

  private destruirGrafico(): void {
    this.limpiarAnimacionEntrada();

    if (this.resizeFrameId !== null) {
      cancelAnimationFrame(this.resizeFrameId);
      this.resizeFrameId = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.ultimoTamanoContenedor = null;
    this.graficoListo = false;
    if (this.grafico) {
      this.grafico.destroy();
      this.grafico = null;
    }
  }
}
